import * as React from "react"
import {
  Card, CardHeader, CardContent, Typography, Grid, Table, TableRow, TableCell, TableBody,
  Chip, Stack, Button, Box, Divider, IconButton, Tooltip, Paper, LinearProgress, Alert, TextField,
  TablePagination,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
} from "@mui/material"
import CloudUploadIcon from "@mui/icons-material/CloudUpload"
import VisibilityIcon from "@mui/icons-material/Visibility"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import { api } from "../../function/axios"

type KBRow = {
  id: string
  title: string
  tags: string[]
  source: string
  bytes: number
  status: "Indexed" | "Processing" | "Failed"
}

export default function KnowledgeBasePanel() {
  const [items, setItems] = React.useState<KBRow[]>([])
  const [loading, setLoading] = React.useState<boolean>(true)
  const [uploading, setUploading] = React.useState<boolean>(false)
  const [error, setError] = React.useState<string | null>(null)
  const [info, setInfo] = React.useState<string | null>(null)
  const [confirmOpen, setConfirmOpen] = React.useState(false)
  const [deleteTarget, setDeleteTarget] = React.useState<KBRow | null>(null)

  const [page, setPage] = React.useState(0)
  const rowsPerPage = 5

  const pagedItems = React.useMemo(
    () => items.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [items, page],
  )

const handleDelete = async () => {
  if (!deleteTarget) return
  try {
    setError(null)

    // 1) Оптимистично убираем строку из таблицы
    setItems(prev => prev.filter(i => i.id !== deleteTarget.id))

    // 2) Удаляем на бэке
    await api({
      method: "DELETE",
      url: `/ai-assistant/kb/${deleteTarget.id}`,
    })

    setInfo(`Removed: ${deleteTarget.title}`)

    // 3) Перечитываем данные для синхронизации статусов/полей
    await refetch()
  } catch (err: any) {
    setError(err.message ?? String(err))
    // На ошибке вернём актуальное состояние с сервера
    await refetch()
  } finally {
    setConfirmOpen(false)
    setDeleteTarget(null)
  }
}

  React.useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(items.length / rowsPerPage) - 1)
    if (page > maxPage) setPage(maxPage)
  }, [items, page])


  const [dragOver, setDragOver] = React.useState(false)
  const [file, setFile] = React.useState<File | null>(null)
  const [tagsInput, setTagsInput] = React.useState<string>("")
  const [titleInput, setTitleInput] = React.useState<string>("")
  const storage = "openai_file_search"

  const formatSize = (bytes: number) => {
    if (!bytes && bytes !== 0) return "—"
    if (bytes < 1024) return `${bytes} B`
    const kb = bytes / 1024
    if (kb < 1024) return `${kb.toFixed(1)} KB`
    const mb = kb / 1024
    return `${mb.toFixed(1)} MB`
  }

  const refetch = React.useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api({
        method: 'GET',
        url: '/ai-assistant/kb',
      })
      if (!res.code) throw new Error(`GET KnowledgeBase → ${res.code}`)
      const data: Array<{
        id: string
        source: string
        handle: string
        title: string
        mime: string
        bytes: number
        tags: string[]
        created_at: string
      }> = await res?.data

      const mapped: KBRow[] = data.map(d => ({
        id: d.id,
        title: d.title,
        tags: d.tags ?? [],
        source: d.source,
        bytes: d.bytes ?? 0,
        status: "Indexed",
      }))
      setItems(mapped)
    } catch (e: any) {
      setError(e.message ?? String(e))
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => { void refetch() }, [refetch])

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] || null
    setFile(f)
    if (f && !titleInput) setTitleInput(f.name)
  }

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragOver(false)
    const f = e.dataTransfer.files?.[0]
    if (f) {
      setFile(f)
      if (!titleInput) setTitleInput(f.name)
    }
  }

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragOver(true)
  }
  const onDragLeave = () => setDragOver(false)

  const handleUpload = async () => {
    if (!file) return
    setError(null)
    setInfo(null)

    const optimistic: KBRow = {
      id: `local-${Date.now()}`,
      title: titleInput || file.name,
      tags: tagsInput ? tagsInput.split(",").map(t => t.trim()).filter(Boolean) : [],
      source: storage,
      bytes: file.size,
      status: "Processing",
    }
    setItems(prev => [...prev, optimistic])
    setUploading(true)

    try {
      const form = new FormData()
      form.append("storage", storage)
      form.append("title", titleInput || file.name)
      form.append("tags", tagsInput || "")
      form.append("file", file)

      const res = await api({
        method: "POST",
        url: "/ai-assistant/kb",
        body: form,
      })
      if (!res.code) throw new Error(`POST upload → ${res.code}`)
      setInfo("File uploaded. Indexing started.")
      setFile(null)
      setTitleInput("")
      setTagsInput("")

      await refetch()
    } catch (e: any) {
      setError(e.message ?? String(e))
      setItems(prev => prev.filter(i => i.id !== optimistic.id))
    } finally {
      setUploading(false)
    }
  }

  const statusChip = (s: KBRow["status"]) => {
    if (s === "Indexed") return <Chip size="small" label="Indexed" color="success" variant="outlined" />
    if (s === "Processing") return <Chip size="small" label="Processing" color="warning" variant="outlined" />
    return <Chip size="small" label="Failed" color="error" variant="outlined" />
  }

  return (
    <Card variant="outlined" sx={{ borderRadius: 2, boxShadow: 0 }}>
      <CardHeader
        title={<Typography variant="subtitle1" fontWeight={600}>Knowledge Base</Typography>}
        subheader={<Typography variant="caption" color="text.secondary">Upload files and manage indexed items</Typography>}
        sx={{ pb: 0.5 }}
      />
      {(loading || uploading) && <LinearProgress />}
      <Divider />

      <CardContent>
        <Stack spacing={2} sx={{ mb: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {info && <Alert severity="success">{info}</Alert>}
        </Stack>

        <Grid container spacing={2}>
          <Grid item xs={12} md={8.5}>
            <Paper variant="outlined" sx={{ overflow: "hidden", borderRadius: 2 }}>
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                  bgcolor: "action.hover",
                  borderTopLeftRadius: 8,
                  borderTopRightRadius: 8,
                }}
              >
                <Stack direction="row" spacing={2}>
                  <Typography variant="caption" sx={{ width: "38%", textAlign: "center" }}>
                    Title
                  </Typography>
                  <Typography variant="caption" sx={{ width: "20%", textAlign: "center" }}>
                    Tags
                  </Typography>
                  <Typography variant="caption" sx={{ width: "16%", textAlign: "center" }}>
                    Source
                  </Typography>
                  <Typography variant="caption" sx={{ width: "10%", textAlign: "center" }}>
                    Size
                  </Typography>
                  <Typography variant="caption" sx={{ width: "10%", textAlign: "center" }}>
                    Status
                  </Typography>
                  <Typography variant="caption" sx={{ width: "6%", textAlign: "center" }}>
                    Actions
                  </Typography>
                </Stack>
              </Box>
              <TableContainer sx={{ overflowX: "auto" }}>
                <Table size="small" sx={{ "& td, & th": { border: 0 } }}>
                  <TableBody>
                    {pagedItems.map((row) => (
                      <TableRow key={row.id} sx={{ "&:not(:last-of-type) td": { borderBottom: theme => `1px solid ${theme.palette.divider}` } }}>
                        <TableCell sx={{ width: "38%", textAlign: "center" }}>
                          {row.title.length > 45 ? (
                            <Tooltip title={row.title} arrow>
                              <Chip
                                size="small"
                                label={`${row.title.slice(0, 45)}…`}
                                color="primary"
                              />
                            </Tooltip>
                          ) : (
                            <Chip size="small" label={row.title} color="primary" />
                          )}
                        </TableCell>

                        <TableCell sx={{ width: "20%" }}>
                          <Stack direction="column" spacing={1} flexWrap="wrap">
                            {row.tags.length ? (
                              row.tags.map((t) => {
                                const long = t.length > 20
                                const short = long ? `${t.slice(0, 20)}…` : t

                                const pillSx = {
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  border: 1,
                                  borderColor: "primary.main",
                                  color: "text.primary",
                                  borderRadius: "9999px",
                                  px: 1.5,
                                  py: 0.5,
                                  fontSize: "0.75rem",
                                  lineHeight: 1.4,
                                  whiteSpace: "nowrap",
                                } as const

                                return long ? (
                                  <Tooltip key={t} title={t} arrow>
                                    <Box component="span" sx={pillSx}>{short}</Box>
                                  </Tooltip>
                                ) : (
                                  <Box key={t} component="span" sx={pillSx}>{short}</Box>
                                )
                              })
                            ) : (
                              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', width: '100%' }}> No tags</Typography>
                            )}
                          </Stack>
                        </TableCell>

                        <TableCell sx={{ width: "16%", textAlign: 'center' }}>
                          <Typography variant="caption" color="text.secondary">{row.source}</Typography>
                        </TableCell>
                        <TableCell sx={{ width: "10%", textAlign: 'center' }}>
                          <Typography variant="caption" color="text.secondary">{formatSize(row.bytes)}</Typography>
                        </TableCell>
                        <TableCell sx={{ width: "10%" }}>
                          <Stack direction="column" spacing={1} flexWrap="wrap">
                            <Box component="span"
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                border: 1,
                                borderColor: "primary.main",
                                color: "text.primary",
                                borderRadius: "9999px",
                                px: 1.5,
                                py: 0.5,
                                fontSize: "0.75rem",
                                lineHeight: 1.4,
                                whiteSpace: "nowrap",
                              }}>
                              {row.status}
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ width: "6%" }}>
                          <Stack direction="row" spacing={0}>
                            <Tooltip title="Preview">
                              <span>
                                <IconButton size="small" disabled >
                                  <VisibilityIcon fontSize="small" color="primary" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Remove">
                              <span>
                                <IconButton size="small"
                                disabled={loading || uploading}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setDeleteTarget(row)
                                    setConfirmOpen(true)
                                  }}
                                >
                                  <DeleteOutlineIcon fontSize="small" color="error" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={items.length}
                page={page}
                onPageChange={(_e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[]}  // скрываем селект выбора
                labelRowsPerPage="Rows per page"
                sx={{
                  borderTop: theme => `1px solid ${theme.palette.divider}`,
                  px: 2,
                }}
              />

            </Paper>
          </Grid>

          <Grid item xs={12} md={3.5}>
            <Box
              onDrop={onDrop}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              sx={{
                border: theme => `2px dashed ${theme.palette.divider}`,
                borderRadius: 2,
                p: 3,
                height: 260,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: dragOver ? "action.hover" : "transparent",
                gap: 1.25,
              }}
            >
              <CloudUploadIcon color="primary" sx={{ fontSize: 36 }} />
              <Typography variant="body2" align="center">Drag & drop PDF/TXT/DOCX here</Typography>
              <Typography variant="caption" color="text.secondary">Storage: {storage}</Typography>

              <TextField
                size="small"
                label="Title"
                value={titleInput}
                onChange={(e) => setTitleInput(e.target.value)}
                fullWidth
              />
              <TextField
                size="small"
                label="Tags (comma-separated)"
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                fullWidth
              />

              <Box>
                <input id="kb-upload" type="file" accept=".pdf,.txt,.doc,.docx" style={{ display: "none" }} onChange={handleInput} />
                <label htmlFor="kb-upload">
                  <Button component="span" variant="contained" size="small" startIcon={<CloudUploadIcon />} sx={{ textTransform: "none" }}>
                    Choose File
                  </Button>
                </label>
              </Box>

              <Typography variant="caption">
                {file ? file.name : "No file chosen"}
              </Typography>

              <Button
                variant="contained"
                size="small"
                onClick={handleUpload}
                disabled={!file || uploading}
                sx={{ textTransform: "none" }}
                startIcon={<CloudUploadIcon />}
              >
                Upload
              </Button>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
      <Dialog
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false)
          setDeleteTarget(null)
        }}
      >
        <DialogTitle>Delete</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Delete Knowledge Base item (<b>{deleteTarget?.title}</b>)?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirmOpen(false)
              setDeleteTarget(null)
            }}
          >
            No
          </Button>
          <Button color="error" onClick={handleDelete}>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  )
}
